import type { ClintplayEvent, ErrorEventData } from '../types';

type EventCallback = (event: ClintplayEvent) => void;

const RESIZE_OBSERVER_THROTTLE_MS = 1_000;
const RESIZE_OBSERVER_SUBSTRING = 'ResizeObserver loop';

export class ErrorTracker {
  private onErrorHandler: OnErrorEventHandler | null = null;
  private onRejectionHandler: ((e: PromiseRejectionEvent) => void) | null = null;
  private previousOnError: OnErrorEventHandler | null = null;
  private lastResizeObserverTime: number = 0;

  constructor(
    private sessionId: string,
    private emit: EventCallback
  ) {}

  private isResizeObserverError(message: string): boolean {
    return message.includes(RESIZE_OBSERVER_SUBSTRING);
  }

  private shouldThrottleResizeObserver(): boolean {
    const now = Date.now();
    const elapsed = now - this.lastResizeObserverTime;
    if (elapsed >= 0 && elapsed < RESIZE_OBSERVER_THROTTLE_MS) {
      return true;
    }
    this.lastResizeObserverTime = now;
    return false;
  }

  start(): void {
    this.previousOnError = window.onerror;

    this.onErrorHandler = (message, source, lineno, colno, error) => {
      const msgStr = String(message);
      const isResizeObserver = this.isResizeObserverError(msgStr);

      if (isResizeObserver) {
        if (this.shouldThrottleResizeObserver()) {
          if (this.previousOnError) {
            try {
              return this.previousOnError.call(window, message, source, lineno, colno, error);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('[Clintplay] previous window.onerror threw', err);
            }
          }
          return false;
        }
      }

      const data: ErrorEventData = {
        message: msgStr,
        stack: error?.stack,
        source: source ?? undefined,
        lineno: lineno ?? undefined,
        colno: colno ?? undefined,
        type: 'onerror',
        level: isResizeObserver ? 'warning' : 'error',
      };

      if (isResizeObserver) {
        data.isLayoutWarning = true;
      }

      this.emit({
        type: 'error',
        timestamp: Date.now(),
        sessionId: this.sessionId,
        data,
      });

      if (this.previousOnError) {
        try {
          return this.previousOnError.call(window, message, source, lineno, colno, error);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[Clintplay] previous window.onerror threw', err);
        }
      }
      return false;
    };

    this.onRejectionHandler = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const reasonStr = reason instanceof Error ? reason.message : String(reason);
      const isResizeObserver = this.isResizeObserverError(reasonStr);

      if (isResizeObserver) {
        if (this.shouldThrottleResizeObserver()) {
          return;
        }
      }

      const data: ErrorEventData = {
        message: reasonStr,
        stack: reason instanceof Error ? reason.stack : undefined,
        type: 'unhandledrejection',
        level: isResizeObserver ? 'warning' : 'error',
      };

      if (isResizeObserver) {
        data.isLayoutWarning = true;
      }

      this.emit({
        type: 'error',
        timestamp: Date.now(),
        sessionId: this.sessionId,
        data,
      });
    };

    window.onerror = this.onErrorHandler;
    window.addEventListener('unhandledrejection', this.onRejectionHandler);
  }

  stop(): void {
    if (this.previousOnError) {
      window.onerror = this.previousOnError;
    } else {
      window.onerror = null;
    }

    if (this.onRejectionHandler) {
      window.removeEventListener('unhandledrejection', this.onRejectionHandler);
    }

    this.onErrorHandler = null;
    this.onRejectionHandler = null;
    this.lastResizeObserverTime = 0;
  }
}
